import type { Communication } from '@medplum/fhirtypes'
import { type WithId } from '@medplum/core'
import { useMedplum } from '@medplum/react'
import { useInfiniteQuery } from '@tanstack/react-query'
import { showNotification } from '@mantine/notifications'
import { useEffect, useMemo } from 'react'
import { logError } from '@/utils/errorUtils'
import { CATEGORY_CODE } from './utils'

const PAGE_SIZE = 50

export const careCoordinationNotesQueryKey = (patientId: string) =>
  ['care-coordination-notes', patientId] as const

export function useCareCoordinationNotes(patientId: string) {
  const medplum = useMedplum()

  const query = useInfiniteQuery({
    queryKey: careCoordinationNotesQueryKey(patientId),
    queryFn: async ({ pageParam }) => {
      const searchParams = [
        `subject=Patient/${patientId}`,
        `category=${CATEGORY_CODE}`,
        '_sort=-_lastUpdated',
        `_count=${PAGE_SIZE}`,
        `_offset=${pageParam}`,
      ].join('&')
      return medplum.search('Communication', searchParams)
    },
    initialPageParam: 0,
    getNextPageParam: (lastPage, allPages) =>
      lastPage.link?.find((l) => l.relation === 'next') ? allPages.length * PAGE_SIZE : undefined,
  })

  useEffect(() => {
    if (query.error) {
      logError('patient', 'Failed to fetch care coordination notes', query.error)
      showNotification({ title: 'Error', message: 'Failed to fetch notes', color: 'red' })
    }
  }, [query.error])

  const notes = useMemo(
    () =>
      (query.data?.pages ?? []).flatMap((page) =>
        (page.entry ?? [])
          .map((e) => e.resource)
          .filter((r): r is WithId<Communication> => r?.resourceType === 'Communication'),
      ),
    [query.data],
  )

  return {
    ...query,
    notes,
    isLoading: query.isFetching && !query.isFetchingNextPage,
  }
}
